import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@crowdex/ui/components/tooltip';
import { cn } from '@crowdex/ui/lib/utils';
import { LayoutGrid, LibraryBig, MessagesSquare, PanelLeft, Plus, Search } from 'lucide-react';
import { type KeyboardEvent, type PointerEvent, type ReactNode, useMemo } from 'react';
import { ItemIcon } from '@/components/item-icon';
import { AccountMenu, useAccountsToReconnect } from '@/components/sidebar/account-menu';
import { setAllAppsOpen, useAllAppsOpen } from '@/components/sidebar/all-apps';
import {
  consumeDragClick,
  type FavoriteSlot,
  FavoritesMenu,
  NO_WINDOW_DRAG,
  useFavoriteDragSource,
  useFavoritesZone,
} from '@/components/sidebar/favorite-drag';
import { type FavoriteItem, useAppOnScreen, useFavorites } from '@/components/sidebar/favorites';
import { openShellSearch } from '@/components/sidebar/search-dialog';
import { useShellNavigation, useSidebarWaiting } from '@/components/sidebar/shell-navigation';
import { NEW_TASK_SHORTCUT } from '@/components/sidebar/shell-shortcuts';
import { NotificationsButton } from '@/features/notifications/notifications-button';
import { SIDEBAR_RAIL_WIDTH } from '@/hooks/use-desktop-sidebar';
import { liveAssistants, openAssistant, useWorkspaceDirectory } from '@/lib/assistants/directory';
import { rowState } from '@/lib/desktop-shell/sidebar-model';
import { SIDEBAR_SEARCH_SHORTCUT } from '@/lib/desktop-shell/sidebar-search';
import { useDesktopShellStore } from '@/lib/desktop-shell/store-context';
import { useLibraryPage, useRouter } from '@/lib/navigation';
import { commandLabel, useShortcutOverrides } from '@/lib/settings-page/keyboard-shortcuts';

/** A rail button's box: 40 px, a finger's 44 px on a touch screen. */
const RAIL_BUTTON =
  'relative flex size-10 shrink-0 items-center justify-center rounded-xl pointer-coarse:size-11';

/** The rail's tooltip: the button's name, then its keys. */
function RailTooltip({ label, shortcut }: { label: string; shortcut?: string }) {
  return (
    <TooltipContent className="flex items-center gap-2" side="right">
      {label}
      {shortcut !== undefined && <kbd className="font-sans text-muted-foreground">{shortcut}</kbd>}
    </TooltipContent>
  );
}

/** A favourite's button on the rail: it drags (see `favorite-drag`), Alt + arrows move it. */
type RailSlot = {
  entry: string;
  state: FavoriteSlot['state'];
  held: boolean;
  onPointerDown: (event: PointerEvent<HTMLButtonElement>) => void;
  onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void;
};

function RailButton({
  label,
  tooltip = label,
  active = false,
  shortcut,
  slot,
  fixed = false,
  onSelect,
  children,
}: {
  label: string;
  /** Its name in the tooltip, when its label says more (a count). */
  tooltip?: string;
  active?: boolean;
  shortcut?: string;
  /** A favourite (it moves, it goes). */
  slot?: RailSlot;
  /** All apps, after the favourites: it stays last. */
  fixed?: boolean;
  onSelect: () => void;
  children: ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          aria-current={active ? 'page' : undefined}
          aria-label={label}
          className={cn(
            RAIL_BUTTON,
            'text-muted-foreground transition-colors hover:bg-control/70 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            active && 'bg-control text-foreground',
            slot !== undefined && 'select-none [-webkit-touch-callout:none]',
            slot?.held && 'bg-control shadow-md',
            slot?.state === 'placeholder' &&
              'border border-dashed border-hairline-strong bg-transparent hover:bg-transparent [&>*]:invisible',
            slot?.state === 'away' && 'hidden',
          )}
          data-favorite-fixed={fixed || undefined}
          data-favorite-placeholder={slot?.state === 'placeholder' || undefined}
          data-favorite-slot={slot === undefined || slot.state === 'away' ? undefined : slot.entry}
          onClick={() => {
            if (slot !== undefined && consumeDragClick()) return;
            onSelect();
          }}
          onKeyDown={slot?.onKeyDown}
          onPointerDown={slot?.onPointerDown}
          type="button"
        >
          {children}
        </button>
      </TooltipTrigger>
      <RailTooltip label={tooltip} {...(shortcut === undefined ? {} : { shortcut })} />
    </Tooltip>
  );
}

/** Another surface's button (its own popover or menu) with the rail's tooltip around it. */
function RailTooltipFor({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="flex">{children}</span>
      </TooltipTrigger>
      <RailTooltip label={label} />
    </Tooltip>
  );
}

/** How many conversations wait on the user, as the sidebar's Needs you lists them. */
export function useNeedsYouCount(): number {
  const navigation = useShellNavigation();
  const waiting = useSidebarWaiting();
  const projects = useWorkspaceDirectory((state) => state.projects);
  const placements = useWorkspaceDirectory((state) => state.placements);
  return useMemo(() => {
    // A project's own conversation is its row, never listed in Needs you.
    const projectConversations = new Set(projects.map((project) => project.conversationId));
    return navigation.threads.filter((thread) => {
      if (projectConversations.has(thread.id)) return false;
      const state = rowState(thread, waiting.get(thread.id), placements[thread.id]);
      return state === 'approval' || state === 'question';
    }).length;
  }, [navigation.threads, waiting, projects, placements]);
}

/**
 * The window's traffic lights (macOS, not full screen) sit in the top bar, as Codex's do with
 * its sidebar collapsed: the rail's top is painted as the content's top bar, so the lights,
 * wider than the rail, never cross its edge, and the rail starts below them (the content's top
 * bar keeps them clear through `--shell-leading-inset`). Its height is the top bar's (`h-14`).
 */
const RAIL_TITLE_BAR = 'h-14';

/**
 * The sidebar collapsed to its rail (D2), when the content needs the width: show the sidebar,
 * New conversation and search at the top; the conversations (Needs you counted); the favourites in their order, All apps
 * last, in one column that scrolls when they don't fit; the notifications and the account at
 * the bottom edge.
 */
export function DesktopNavRail({
  account,
  onExpand,
}: {
  account: { name: string; email: string; profile: string };
  onExpand: () => void;
}) {
  // Its tooltips show the user's keys.
  useShortcutOverrides();
  const navigation = useShellNavigation();
  const { leave } = useLibraryPage();
  const needsYou = useNeedsYouCount();
  const trafficLights = useDesktopShellStore((state) => state.macTitleBarInset) > 0;
  const reconnect = useAccountsToReconnect(!navigation.preview);
  return (
    <TooltipProvider delayDuration={200}>
      <div
        className="flex shrink-0 flex-col border-hairline border-r"
        style={{ width: `${SIDEBAR_RAIL_WIDTH}px` }}
      >
        {trafficLights && (
          <div
            className={cn(RAIL_TITLE_BAR, 'w-full shrink-0 bg-desktop-sidebar')}
            data-electron-drag-region
            data-rail-title-bar
          />
        )}
        <nav
          aria-label="App navigation"
          className="desktop-sidebar type-ui relative flex min-h-0 w-full flex-1 flex-col items-center gap-1 bg-desktop-sidebar pt-2 pb-3"
        >
          <div className="absolute inset-x-0 top-0 h-2" data-electron-drag-region />
          <RailButton label="Show sidebar" onSelect={onExpand}>
            <PanelLeft className="size-[18px]" />
          </RailButton>
          <RailButton
            label="New conversation"
            onSelect={() => {
              setAllAppsOpen(false);
              leave();
              navigation.newTask();
            }}
            shortcut={commandLabel('new-task', NEW_TASK_SHORTCUT)}
          >
            <Plus className="size-[18px]" />
          </RailButton>
          <RailButton
            label="Search"
            onSelect={() => openShellSearch()}
            shortcut={commandLabel('search', SIDEBAR_SEARCH_SHORTCUT)}
          >
            <Search className="size-[18px]" />
          </RailButton>
          <span className="my-1.5 h-px w-6 shrink-0 bg-hairline" />
          <RailAssistants />
          {/* The conversations: the sidebar opens on them, Needs you first; its count in amber. */}
          <RailButton
            label={needsYou === 0 ? 'Conversations' : `Conversations, ${needsYou} waiting on you`}
            onSelect={onExpand}
            tooltip={
              needsYou === 0 ? 'Conversations' : `Conversations · ${needsYou} waiting on you`
            }
          >
            <MessagesSquare className="size-[18px]" />
            {needsYou > 0 && (
              <span
                aria-hidden
                className="type-caption absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-attention px-1 font-semibold text-background leading-none ring-2 ring-desktop-sidebar"
                data-needs-you-count
              >
                {needsYou > 9 ? '9+' : needsYou}
              </span>
            )}
          </RailButton>
          <span className="my-1.5 h-px w-6 shrink-0 bg-hairline" />
          {/* The favourites in their order, then All apps: one column, scrolling when short. */}
          <RailFavorites />
          {/* Takes what is left (basis 0): the favourites shrink and scroll before it does. */}
          <div className="min-h-2 w-full flex-1 basis-0" data-electron-drag-region />
          {/* Pinned to the bottom edge: the notifications, the account. */}
          <RailTooltipFor label="Notifications">
            <NotificationsButton className={RAIL_BUTTON} variant="count" />
          </RailTooltipFor>
          <RailTooltipFor label="Account">
            <AccountMenu
              className={RAIL_BUTTON}
              email={account.email}
              name={account.name}
              profile={account.profile}
              reconnect={reconnect}
              side="right"
            />
          </RailTooltipFor>
        </nav>
      </div>
    </TooltipProvider>
  );
}

/** The assistants, the home one first: each opens its channel, as its sidebar row does. */
function RailAssistants() {
  const navigation = useShellNavigation();
  const projects = useWorkspaceDirectory((state) => state.projects);
  const assistants = useMemo(() => liveAssistants({ projects }), [projects]);
  return assistants.map((assistant) => (
    <RailButton
      active={
        assistant.conversationId !== null && assistant.conversationId === navigation.activeThreadId
      }
      key={assistant.id}
      label={assistant.name}
      onSelect={() => void openAssistant(assistant, navigation.openConversation)}
    >
      <span
        aria-hidden
        className="flex size-[18px] items-center justify-center rounded-[5px] font-semibold text-foreground text-micro leading-none"
        data-rail-assistant={assistant.id}
        style={{ backgroundColor: assistant.color }}
      >
        {assistant.name.trim()[0]?.toUpperCase() ?? '?'}
      </span>
    </RailButton>
  ));
}

/**
 * The rail's favourites, in their order, then All apps: one column that scrolls when short. They
 * drag as the sidebar's tiles do (out of the column: unpinned); right-click for Move and Unpin.
 */
function RailFavorites() {
  const router = useRouter();
  const { pathname, leave } = useLibraryPage();
  const allAppsOpen = useAllAppsOpen();
  const shownApp = useAppOnScreen();
  const favorites = useFavorites();
  const zone = useFavoritesZone(favorites, { layout: 'column' });
  const drag = useFavoriteDragSource(favorites);
  const icon = (item: FavoriteItem) =>
    item.kind === 'library' ? (
      <LibraryBig className="size-[18px]" />
    ) : (
      <ItemIcon className="size-[18px]" icon={item.app.icon} src={item.app.iconSrc} />
    );
  return (
    <>
      <FavoritesMenu favorites={favorites} layout="column" zone={zone}>
        <div
          className="flex min-h-0 w-full flex-col items-center gap-1 overflow-y-auto py-0.5 [scrollbar-width:none]"
          data-electron-no-drag-region
          data-rail-favorites
          ref={zone.ref}
          style={NO_WINDOW_DRAG}
        >
          {zone.slots.map(({ entry, item, state }) =>
            item === null ? (
              // The place an app dragged from All apps would take.
              <span
                aria-hidden
                className={cn(RAIL_BUTTON, 'border border-dashed border-hairline-strong')}
                data-favorite-placeholder
                data-favorite-slot={entry}
                key={entry}
              />
            ) : (
              <RailButton
                active={
                  !allAppsOpen &&
                  (item.kind === 'library' ? pathname === '/artifacts' : shownApp === item.app.view)
                }
                key={entry}
                label={item.title}
                onSelect={() => {
                  setAllAppsOpen(false);
                  if (item.kind === 'library') router.push('/artifacts');
                  else favorites.openApp(item.app.view);
                }}
                slot={{
                  entry,
                  state,
                  held: drag.held === entry,
                  onKeyDown: (event) => zone.onKeyDown(event, item),
                  onPointerDown: (event) =>
                    drag.begin(event, {
                      entry,
                      title: item.title,
                      icon: icon(item),
                      from: 'favorites',
                    }),
                }}
              >
                {icon(item)}
              </RailButton>
            ),
          )}
          <RailButton
            active={allAppsOpen}
            fixed
            label="All apps"
            onSelect={() => {
              leave();
              setAllAppsOpen(!allAppsOpen);
            }}
          >
            <LayoutGrid className="size-[18px]" />
          </RailButton>
        </div>
      </FavoritesMenu>
      {zone.announcement}
      {drag.ghost}
    </>
  );
}
